"use client";

import React, { useEffect, useState } from "react";
import useTemplateBase from "./TemplateBase";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Check,
  X,
  Plus,
  ImageIcon,
  Loader2,
  Tag,
  Home,
  Building2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import profile from "../../../public/profilePicture/profile.webp";
import openHouseBadge from "../../assets/star_red.png";

interface Property {
  id: number;
  propertyListType?: { id: number; list_name: string; custom_template_data?: Record<string, any> };
  title: string;
  listing_id?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  photos?: string[] | null;
  is_open_house?: boolean;
}

interface PropertyList {
  id: number;
  list_name: string;
}

interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

interface Template11Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Partial<any>) => void;
  onPropertyListChange?: (selectedList: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  selectedList?: string;
  readOnly?: boolean;
}

interface FeatureItem {
  id?: number;
  feature_name: string;
}

const Template11: React.FC<Template11Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  selectedList: viewSelectedList,
  onPropertyListCreated,
  readOnly = false,
}) => {
  const { toast } = useToast();

  const [isCustomList, setIsCustomList] = useState(false);
  const [customListName, setCustomListName] = useState("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] = useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<string | null>(null);
  const [roomLabels, setRoomLabels] = useState<string[]>(["KITCHEN", "LIVING ROOM", "BEDROOM"]);

  const thumbnailRefs = [
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
  ];

  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePropertyList,
    updatePhotoAtIndex,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  useEffect(() => { setLocalPropertyLists(propertyLists); }, [propertyLists]);
  useEffect(() => { setLocalSelectedListOverride(null); }, [property?.id]);

  const handleRoomLabelChange = (index: number, value: string) => {
    if (readOnly) return;
    setRoomLabels((prev) => { const u = [...prev]; u[index] = value; return u; });
    onDirtyChange?.(true);
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Image must be less than 10 MB", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setFlyerData((prev) => ({ ...prev, heroImage: result }));
      onDirtyChange?.(true);
      onChange?.({ heroImage: result });
    };
    reader.readAsDataURL(file);
  };

  const handleThumbnailChange = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Image must be less than 10 MB", variant: "destructive" });
      return;
    }
    const slotName = index === 0 ? "photo2" : index === 1 ? "photo3" : "photo4";
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      setFlyerData((prev) => ({ ...prev, [slotName]: dataUrl }));
      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);
        const uploadRes = await axiosInstance.post(endpoints.mls_uploads, formData, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "Content-Type": "multipart/form-data" },
        });
        if (uploadRes?.data?.url) {
          await updatePhotoAtIndex(uploadRes.data.url, slotName as "photo2" | "photo3" | "photo4");
          toast({ title: "Success", description: "Photo uploaded successfully", duration: 1000 });
        }
      } catch (err) {
        toast({ title: "Error", description: "Failed to upload photo", variant: "destructive" });
      }
    };
    reader.readAsDataURL(file);
  };

  const selectedList =
    localSelectedListOverride ?? viewSelectedList ?? externalSelectedList ??
    selectedPropertyList ?? property?.propertyListType?.list_name ?? "ELEGANT";

  const allLists = localPropertyLists.length > 0 ? localPropertyLists : [
    { id: 1, list_name: "ELEGANT" },
    { id: 2, list_name: "LUXURY" },
    { id: 3, list_name: "MODERN" },
    { id: 4, list_name: "CLASSIC" },
  ];

  const saveCustomList = async () => {
    if (readOnly) return;
    const trimmedName = customListName.trim();
    if (!trimmedName) { toast({ title: "Error", description: "Please enter a name for your custom tag", variant: "destructive" }); return; }
    if (!property?.id) { toast({ title: "Error", description: "No property selected", variant: "destructive" }); return; }

    const existingList = localPropertyLists.find((l) => l.list_name.toLowerCase() === trimmedName.toLowerCase());
    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false); setCustomListName("");
      toast({ title: "List Selected", description: `"${existingList.list_name}" selected` });
      return;
    }

    setIsSavingCustomList(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const createResponse = await axiosInstance.post(endpoints.propertylisttype, { list_name: trimmedName }, { headers });
      const newListType: PropertyList = { id: createResponse.data.id, list_name: createResponse.data.list_name };
      setLocalSelectedListOverride(newListType.list_name);
      setLocalPropertyLists((prev) => [...prev, newListType]);
      setIsCustomList(false); setCustomListName("");
      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);
      toast({ title: "Custom Tag Created", description: `"${newListType.list_name}" created` });
    } catch { toast({ title: "Error", description: "Failed to create custom tag", variant: "destructive" }); }
    finally { setIsSavingCustomList(false); }
  };

  const cancelCustomList = () => { setIsCustomList(false); setCustomListName(""); };

  const handleListSelection = (value: string) => {
    if (readOnly) return;
    if (value === "__custom__") { setIsCustomList(true); setCustomListName(""); return; }
    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(property?.is_open_house ?? false);
  useEffect(() => { setIsOpenHouseConfirmed(property?.is_open_house ?? false); }, [property?.is_open_house]);

  const images = [flyerData.heroImage || "", flyerData.photo2 || "", flyerData.photo3 || "", flyerData.photo4 || ""];

  return (
    <div className="w-full max-w-full bg-white border border-gray-200 rounded-xl overflow-hidden font-arial">

      {/* ── Header with Company Branding ── */}
      <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 bg-white border-b border-gray-100 gap-2">
        {/* Company name */}
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="flex-shrink-0 w-0 h-0"
            style={{ borderLeft: "10px solid transparent", borderRight: "10px solid transparent", borderBottom: "16px solid #4a6741" }}
          />
          <span className="text-[#b8860b] text-xs sm:text-sm font-bold uppercase tracking-widest truncate">
            {agentData?.company || "Premium Estates"}
          </span>
        </div>

        {/* Tag Selector */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <Tag className="h-3 w-3 sm:h-4 sm:w-4 text-[#4a6741] flex-shrink-0" />
          {readOnly ? (
            <span
              className="bg-[#4a6741]/20 text-[#4a6741] px-2 sm:px-4 py-1 sm:py-1.5 rounded text-[10px] sm:text-sm font-semibold truncate max-w-[90px] sm:max-w-[160px]"
              title={selectedList || property?.propertyListType?.list_name || "ELEGANT"}
            >
              {selectedList || property?.propertyListType?.list_name || "ELEGANT"}
            </span>
          ) : isCustomList ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={customListName}
                onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                placeholder="Custom tag"
                maxLength={20}
                className="bg-[#4a6741]/20 text-[#4a6741] placeholder-[#4a6741]/60 px-2 py-1 rounded text-[10px] sm:text-sm outline-none border border-[#4a6741]/30 w-20 sm:w-32"
                autoFocus
                disabled={isSavingCustomList}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isSavingCustomList) { e.preventDefault(); saveCustomList(); }
                  if (e.key === "Escape") cancelCustomList();
                }}
              />
              <button onClick={saveCustomList} disabled={!customListName.trim() || isSavingCustomList} className="p-1 bg-[#4a6741]/20 rounded hover:bg-[#4a6741]/30 disabled:opacity-50">
                {isSavingCustomList ? <Loader2 className="h-3 w-3 text-[#4a6741] animate-spin" /> : <Check className="h-3 w-3 text-[#4a6741]" />}
              </button>
              <button onClick={cancelCustomList} disabled={isSavingCustomList} className="p-1 bg-[#4a6741]/20 rounded hover:bg-[#4a6741]/30">
                <X className="h-3 w-3 text-[#4a6741]" />
              </button>
            </div>
          ) : (
            <select
              value={selectedList}
              onChange={(e) => handleListSelection(e.target.value)}
              className="bg-[#4a6741]/20 text-[#4a6741] px-2 sm:px-4 py-1 sm:py-1.5 rounded text-[10px] sm:text-sm font-semibold border-none outline-none cursor-pointer max-w-[100px] sm:max-w-none"
            >
              {allLists.map((list) => (
                <option key={list.id} value={list.list_name} className="text-gray-900 bg-white">{list.list_name}</option>
              ))}
              <option value="__custom__" className="text-gray-900 bg-white">+ Add Custom</option>
            </select>
          )}
        </div>
      </div>

      {/* ── Main Hero Section ── */}
      <div className="relative">
        <div className="relative overflow-hidden group" style={{ minHeight: "220px" }}>
          {/* Open House Badge */}
          {isOpenHouseConfirmed && (
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-30">
              <div className="relative w-14 sm:w-20 md:w-24">
                <img src={openHouseBadge} alt="Open House" className="w-full drop-shadow-2xl" />
                <span className="absolute inset-0 flex items-center justify-center text-white font-extrabold text-[8px] sm:text-[10px] md:text-xs tracking-wide text-center pointer-events-none leading-tight">
                  OPEN<br />HOUSE
                </span>
              </div>
            </div>
          )}

          {/* Hero Image */}
          {flyerData.heroImage ? (
            <img src={flyerData.heroImage} alt="Main property" className="w-full h-full object-cover" style={{ minHeight: "220px", maxHeight: "420px" }} />
          ) : (
            <div className="w-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center" style={{ minHeight: "220px" }}>
              <ImageIcon className="h-14 w-14 sm:h-20 sm:w-20 text-gray-400 mb-2 sm:mb-3" />
              <p className="text-xs sm:text-sm text-gray-500">{readOnly ? "No image" : "Upload main property photo"}</p>
            </div>
          )}

          {/* Green Gradient Overlay — constrained width + ellipsis on mobile */}
          <div
            className="absolute inset-0 flex flex-col justify-end sm:justify-center pb-4 sm:pb-0 px-3 sm:px-6 md:px-8"
          style={{
  background: "linear-gradient(to right, rgba(74,103,65,0.75) 0%, rgba(74,103,65,0.65) 25%, rgba(74,103,65,0.35) 45%, rgba(74,103,65,0.1) 60%, rgba(74,103,65,0) 70%)",
}}
          >
            {/* Headline */}
            {readOnly ? (
            <p
  className="
    text-white font-bold uppercase tracking-wider leading-tight
    text-xl sm:text-2xl md:text-3xl
    max-w-[85%] sm:max-w-[70%] md:max-w-[55%]
  "
>
  {flyerData.headline || "LUXURY HOME"}
</p>
            ) : (
              <input
  type="text"
  value={flyerData.headline || ""}
  onChange={(e) => handleInputChange("headline", e.target.value)}
  className="
    text-white font-bold bg-transparent border-none outline-none uppercase tracking-wider
    text-xl sm:text-2xl md:text-3xl
    w-full max-w-[85%] sm:max-w-[70%] md:max-w-[55%]
  "
  placeholder="LUXURY HOME"
/>
            )}

            {/* FOR SALE */}
            <span
              className="font-bold uppercase tracking-wider text-lg sm:text-xl md:text-2xl lg:text-3xl"
              style={{ color: "#c9a227", textShadow: "2px 2px 4px rgba(0,0,0,0.3)" }}
            >
              FOR SALE
            </span>
          </div>

          {/* Upload Button */}
          {!readOnly && (
            <Button
              size="sm"
              variant="outline"
              className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-white/95 hover:bg-white text-[10px] sm:text-xs shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
              Change
            </Button>
          )}
          {!readOnly && (
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleMainImageChange} />
          )}
        </div>
      </div>

      {/* ── Room Thumbnails ── */}
      <div className="bg-gray-50 px-3 sm:px-4 py-3 sm:py-4 border-t border-gray-100">
        <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-2xl mx-auto">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className={`relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-200 shadow-md group ${!readOnly ? "cursor-pointer hover:shadow-lg" : ""} transition-shadow`}
              onClick={() => !readOnly && thumbnailRefs[index].current?.click()}
            >
              {images[index + 1] ? (
                <img src={images[index + 1]} alt={`Room ${index + 1}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                  <ImageIcon className="h-5 w-5 sm:h-7 sm:w-7 md:h-8 md:w-8 text-gray-400 mb-0.5" />
                  <span className="text-[9px] sm:text-xs text-gray-500">{readOnly ? "No photo" : "Photo"}</span>
                </div>
              )}

              {!readOnly && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center">
                  <Upload className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-white mb-0.5" />
                  <span className="text-[9px] sm:text-xs text-white font-medium">{images[index + 1] ? "Change" : "Upload"}</span>
                </div>
              )}

              <div className="absolute bottom-0 left-0 right-0 p-1 sm:p-2">
                {readOnly ? (
                  <p className="text-white text-[8px] sm:text-xs font-semibold w-full text-center uppercase tracking-wide" />
                ) : (
                  <input
                    type="text"
                    value={roomLabels[index]}
                    onChange={(e) => handleRoomLabelChange(index, e.target.value.toUpperCase())}
                    onClick={(e) => e.stopPropagation()}
                    className="text-white text-[8px] sm:text-xs font-semibold bg-transparent border-none outline-none w-full text-center uppercase tracking-wide"
                    placeholder="ROOM"
                  />
                )}
              </div>

              {!readOnly && (
                <input ref={thumbnailRefs[index]} type="file" accept="image/*" className="hidden" onChange={(e) => handleThumbnailChange(index, e)} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Content Section ── */}
      <div className="bg-white pt-4 sm:pt-6 pb-4 sm:pb-6 px-3 sm:px-6">

        {/* Property Stats — always 3-col but scaled */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
          {/* Beds */}
          <div className="text-center bg-gradient-to-br from-[#4a6741]/5 to-[#4a6741]/10 rounded-xl p-2 sm:p-4 border border-[#4a6741]/20">
            <Bed className="h-4 w-4 sm:h-6 sm:w-6 text-[#4a6741] mx-auto mb-1 sm:mb-2" />
            {readOnly ? (
              <p className="text-lg sm:text-2xl font-bold text-gray-900 text-center mb-0.5 sm:mb-1">{flyerData.bedrooms ?? 0}</p>
            ) : (
              <input type="number" min="0" value={flyerData.bedrooms ?? ""} onChange={(e) => handleInputChange("bedrooms", e.target.value ? parseInt(e.target.value) : null)}
                className="text-lg sm:text-2xl font-bold text-gray-900 w-full text-center bg-transparent border-none outline-none mb-0.5 sm:mb-1 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]" placeholder="0" />
            )}
            <p className="text-[9px] sm:text-xs text-gray-600 uppercase font-medium">Beds</p>
          </div>

          {/* Baths */}
          <div className="text-center bg-gradient-to-br from-[#4a6741]/5 to-[#4a6741]/10 rounded-xl p-2 sm:p-4 border border-[#4a6741]/20">
            <Bath className="h-4 w-4 sm:h-6 sm:w-6 text-[#4a6741] mx-auto mb-1 sm:mb-2" />
            {readOnly ? (
              <p className="text-lg sm:text-2xl font-bold text-gray-900 text-center mb-0.5 sm:mb-1">{flyerData.bathrooms ?? 0}</p>
            ) : (
              <input type="number" value={flyerData.bathrooms ?? ""} onChange={(e) => handleInputChange("bathrooms", e.target.value ? parseInt(e.target.value) : null)}
                className="text-lg sm:text-2xl font-bold text-gray-900 w-full text-center bg-transparent border-none outline-none mb-0.5 sm:mb-1 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]" placeholder="0" />
            )}
            <p className="text-[9px] sm:text-xs text-gray-600 uppercase font-medium">Baths</p>
          </div>

          {/* Sq Ft */}
          <div className="text-center bg-gradient-to-br from-[#4a6741]/5 to-[#4a6741]/10 rounded-xl p-2 sm:p-4 border border-[#4a6741]/20">
            <Square className="h-4 w-4 sm:h-6 sm:w-6 text-[#4a6741] mx-auto mb-1 sm:mb-2" />
            {readOnly ? (
              <p className="text-lg sm:text-2xl font-bold text-gray-900 text-center mb-0.5 sm:mb-1 break-all">{flyerData.sqft ?? flyerData.area ?? 0}</p>
            ) : (
              <input type="number" value={flyerData.sqft ?? flyerData.area ?? ""} onChange={(e) => handleInputChange("sqft", e.target.value ? parseInt(e.target.value) : null)}
                className="text-lg sm:text-2xl font-bold text-gray-900 w-full text-center bg-transparent border-none outline-none mb-0.5 sm:mb-1 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]" placeholder="0" />
            )}
            <p className="text-[9px] sm:text-xs text-gray-600 uppercase font-medium">Sq Ft</p>
          </div>
        </div>

        {/* HOME FEATURES Title */}
        <div className="mb-4 sm:mb-5 text-center">
          <span className="text-gray-900 font-bold text-lg sm:text-xl tracking-wide">HOME </span>
          <span className="font-bold text-lg sm:text-xl tracking-wide" style={{ color: "#4a6741" }}>FEATURES</span>
          <div className="w-12 sm:w-16 h-1 bg-[#4a6741] mx-auto mt-1.5 sm:mt-2 rounded-full" />
        </div>

        {/* Features */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase">Property Features</h3>
            {!readOnly && <span className="text-[10px] sm:text-xs text-gray-500">{flyerData.features?.length || 0}/10</span>}
          </div>

          <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
            {(flyerData.features || []).map((feature: FeatureItem, index: number) => (
              <div key={feature.id ?? index}
                className="flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-[#4a6741]/5 to-transparent rounded-lg p-2 sm:p-3 group hover:from-[#4a6741]/10 transition-colors border border-[#4a6741]/10 min-w-0">
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#4a6741" }}>
                  <Check className="h-3 w-3 sm:h-4 sm:w-4 text-white" strokeWidth={3} />
                </div>
                {readOnly ? (
                  <span className="text-xs sm:text-sm text-gray-800 flex-1 font-medium truncate">{feature.feature_name || "Feature"}</span>
                ) : (
                  <>
                    <input type="text" value={feature.feature_name} onChange={(e) => handleFeatureChange(index, e.target.value.slice(0, 35))}
                      onBlur={() => handleFeatureBlur(index)} maxLength={30} placeholder="Feature (max 30 chars)..."
                      className="bg-transparent border-none outline-none text-xs sm:text-sm text-gray-800 flex-1 font-medium min-w-0" />
                    <button onClick={() => handleFeatureDelete(index)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 flex-shrink-0">
                      <X className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          {!readOnly && (flyerData.features?.length || 0) < 10 && (
            <Button size="sm" variant="outline" onClick={addFeature}
              className="w-full text-xs sm:text-sm border-dashed" style={{ borderColor: "#4a6741", color: "#4a6741" }}>
              <Plus className="h-3 w-3 mr-1" />Add Feature
            </Button>
          )}
        </div>

        {/* Price Card */}
        <div className="rounded-xl px-4 sm:px-6 py-4 sm:py-5 text-white mb-4 sm:mb-6 shadow-lg" style={{ backgroundColor: "#4a6741" }}>
          <p className="text-[10px] sm:text-xs uppercase tracking-wider mb-1 sm:mb-2 opacity-90 font-semibold text-center">Price</p>
          {readOnly ? (
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-center break-all leading-tight">
              {flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : "$0"}
            </p>
          ) : (
            <input
              type="text"
              value={flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : ""}
              onChange={(e) => { const num = e.target.value.replace(/[^0-9]/g, ""); handleInputChange("price", num === "" ? null : Number(num)); }}
              className="text-xl sm:text-2xl md:text-3xl font-bold bg-transparent border-none outline-none w-full text-center"
              placeholder="$725,000"
            />
          )}
        </div>

        {/* Description */}
        <div className="mb-4 sm:mb-6">
          {readOnly ? (
            <p
              className="w-full bg-white border border-gray-200 rounded-lg p-3 sm:p-4 text-xs sm:text-sm text-gray-700 leading-relaxed
                line-clamp-3 sm:line-clamp-4 md:line-clamp-none"
              title={flyerData.description || ""}
            >
              {flyerData.description || "No description available"}
            </p>
          ) : (
            <textarea
              value={flyerData.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-lg p-3 sm:p-4 outline-none resize-none text-xs sm:text-sm text-gray-700 leading-relaxed"
              rows={3}
              placeholder="Property description..."
            />
          )}
        </div>

        {/* ── Agent Footer ── */}
        <div className="bg-slate-50 rounded-xl px-3 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-6 border border-gray-200">
          {/*
            Layout:
            - Mobile: avatars left, info right (side-by-side, compact)
            - sm+: avatars larger, info with more spacing
          */}
          <div className="flex items-start gap-3 sm:gap-5">

            {/* Avatars: stacked vertically on all sizes */}
            <div className="flex flex-row sm:flex-col items-center gap-2 flex-shrink-0">
              {/* Avatars — AFTER (row layout like the reference image) */}
              <div className="flex flex-row items-center gap-2 flex-shrink-0 mt-5">
                {/* Agent Avatar */}
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-2 border-[#4a6741]/20 overflow-hidden flex-shrink-0">
                  <img src={agentData?.avatar || profile} alt="Agent" className="w-full h-full object-cover" />
                </div>
                {/* Company Logo */}
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-gray-200 bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
                  {agentData?.company_logo ? (
                    <img src={agentData.company_logo} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="h-6 w-6 sm:h-7 sm:w-7 text-gray-300" />
                  )}
                </div>
              </div>
            </div>

            {/* Agent Info */}
            <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
              {/* Agent Name */}
              {readOnly ? (
                <p
                  className="text-base sm:text-xl md:text-2xl font-bold text-[#4a6741] mb-1 leading-tight truncate w-full"
                  title={flyerData.agentName || "Agent Name"}
                >
                  {flyerData.agentName || "Agent Name"}
                </p>
              ) : (
                <input
                  type="text"
                  value={flyerData.agentName || ""}
                  onChange={(e) => handleInputChange("agentName", e.target.value)}
                  className="bg-transparent text-base sm:text-xl md:text-2xl font-bold text-[#4a6741] outline-none mb-1 w-full min-w-0 truncate"
                  placeholder="Agent Name"
                />
              )}

              {/* Phone */}
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1 min-w-0 overflow-hidden">
                <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-[#4a6741] flex-shrink-0" />
                {readOnly ? (
                  <span className="text-xs sm:text-sm text-gray-700 truncate min-w-0 flex-1" title={flyerData.agentPhone || "N/A"}>
                    {flyerData.agentPhone || "N/A"}
                  </span>
                ) : (
                  <input type="text" value={flyerData.agentPhone || ""}
                    onChange={(e) => handleInputChange("agentPhone", e.target.value)}
                    className="bg-transparent text-xs sm:text-sm text-gray-700 outline-none min-w-0 flex-1 truncate"
                    placeholder="Phone" />
                )}
              </div>

              {/* Company */}
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1 min-w-0 overflow-hidden">
                <Building2 className="h-3 w-3 sm:h-4 sm:w-4 text-[#4a6741] flex-shrink-0" />
                {readOnly ? (
                  <span className="text-xs sm:text-sm text-gray-700 truncate min-w-0 flex-1"
                    title={flyerData.company || agentData?.company || "N/A"}>
                    {flyerData.company || agentData?.company || "N/A"}
                  </span>
                ) : (
                  <input type="text" value={flyerData.company || agentData?.company || ""}
                    onChange={(e) => handleInputChange("company", e.target.value)}
                    className="bg-transparent text-xs sm:text-sm text-gray-700 outline-none min-w-0 flex-1 truncate"
                    placeholder="Company" />
                )}
              </div>


              {/* Email */}
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 overflow-hidden">
                <Mail className="h-3 w-3 sm:h-4 sm:w-4 text-[#4a6741] flex-shrink-0" />
                {readOnly ? (
                  <span className="text-xs sm:text-sm text-gray-700 truncate min-w-0 flex-1"
                    title={flyerData.agentEmail || "N/A"}>
                    {flyerData.agentEmail || "N/A"}
                  </span>
                ) : (
                  <input type="text" value={flyerData.agentEmail || ""}
                    onChange={(e) => handleInputChange("agentEmail", e.target.value)}
                    className="bg-transparent text-xs sm:text-sm text-gray-700 outline-none min-w-0 flex-1 truncate"
                    placeholder="Email" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom Decorative Wave ── */}
      <div className="h-6 sm:h-8 relative overflow-hidden" style={{ backgroundColor: "#4a6741" }}>
        <svg className="absolute bottom-0 left-0 w-full h-full block" viewBox="0 0 400 32" preserveAspectRatio="none">
          <path d="M0,32 L0,16 Q5,0 10,16 Q15,4 20,18 Q25,6 30,16 Q35,0 40,18 Q45,8 50,16 Q55,0 60,18 Q65,6 70,16 Q75,4 80,18 Q85,0 90,16 Q95,8 100,18 Q105,4 110,16 Q115,0 120,18 Q125,6 130,16 Q135,4 140,18 Q145,0 150,16 Q155,8 160,18 Q165,4 170,16 Q175,0 180,18 Q185,6 190,16 Q195,4 200,18 Q205,0 210,16 Q215,8 220,18 Q225,4 230,16 Q235,0 240,18 Q245,6 250,16 Q255,4 260,18 Q265,0 270,16 Q275,8 280,18 Q285,4 290,16 Q295,0 300,18 Q305,6 310,16 Q315,4 320,18 Q325,0 330,16 Q335,8 340,18 Q345,4 350,16 Q355,0 360,18 Q365,6 370,16 Q375,4 380,18 Q385,0 390,16 Q395,8 400,18 L400,32 Z" fill="#3d5a35" />
        </svg>
      </div>
    </div>
  );
};

export default Template11;